import React from "react";
import {
  ScheduleWorkingHoursViolation,
  ScheduleWorkingHoursViolationReason,
} from "@myapp/contract";
import Modal from "../../overlay/Modal/Modal";
import { Button } from "../../controls/Button/Button";

/**
 * "The technician does not work then — plan it anyway?" Reached through a 409
 * `SCHEDULE_OUTSIDE_WORKING_HOURS`, whose payload is one violation per offending day per
 * technician. Confirming resends the same request with `allowOutsideWorkingHours: true`.
 *
 * Deliberately not ConflictDialog: nothing occupies the slot, so there is no entry to name — the
 * rows carry a day and a shift window instead of a label and a range.
 */

const REASON_LABELS: Record<ScheduleWorkingHoursViolationReason, string> = {
  NON_WORKING_DAY: "Arbeitsfreier Tag",
  OUTSIDE_SHIFT: "Außerhalb der Schicht",
};

const fmtDay = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/** The shift the block missed, or nothing at all on a day the technician does not work. */
function describeShift(violation: ScheduleWorkingHoursViolation): string {
  if (!violation.shiftStartsAt || !violation.shiftEndsAt) return "Kein Dienst an diesem Tag";
  return `Schicht ${fmtTime(violation.shiftStartsAt)} – ${fmtTime(violation.shiftEndsAt)}`;
}

type Props = {
  open: boolean;
  violations: ScheduleWorkingHoursViolation[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  description?: string;
  confirmLabel?: string;
};

const DEFAULT_DESCRIPTION =
  "Der geplante Zeitraum liegt außerhalb der Arbeitszeit des Technikers. Möchten Sie trotzdem " +
  "planen?";

export const WorkingHoursDialog: React.FC<Props> = ({
  open,
  violations,
  onConfirm,
  onCancel,
  isLoading = false,
  description = DEFAULT_DESCRIPTION,
  confirmLabel = "Trotzdem planen",
}) => {
  return (
    <Modal
      empty
      show={open}
      setShow={(val) => { if (!val && !isLoading) onCancel(); }}
      // `empty` skips the header, so the dialog has no title to be labelled by.
      ariaLabel="Außerhalb der Arbeitszeit"
      className="flex max-h-[90vh] w-[520px] flex-col gap-4 overflow-hidden p-6"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 7v5l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
              stroke="#D97706"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="text-base font-semibold text-gray-900">
          Außerhalb der Arbeitszeit
        </p>
      </div>

      <p className="text-sm text-gray-600">{description}</p>

      <div className="flex-1 overflow-y-auto rounded-md border border-gray-200 bg-gray-50">
        {violations.map((violation, i) => (
          <div
            key={`${violation.reason}:${violation.technicianId}:${violation.date}`}
            className={`flex items-start justify-between gap-2 px-3 py-2 text-sm ${
              i > 0 ? "border-t border-gray-200" : ""
            }`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                  {REASON_LABELS[violation.reason]}
                </span>
                <p className="truncate font-medium text-gray-800">{fmtDay(violation.date)}</p>
              </div>
              <p className="text-xs text-gray-500">{violation.technicianName}</p>
            </div>
            <span className="flex-shrink-0 text-xs text-gray-500">
              {describeShift(violation)}
            </span>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          className="flex-1 rounded-[30px] bg-amber-600 font-bold text-white h-[46px]"
          type="button"
        >
          {isLoading ? "Speichern..." : confirmLabel}
        </Button>
        <Button
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 rounded-[30px] bg-[#716D6B] font-bold text-white h-[46px]"
          type="button"
        >
          Abbrechen
        </Button>
      </div>
    </Modal>
  );
};

export default WorkingHoursDialog;
